import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { ListBase, useGetIdentity, useListContext } from "ra-core";
import { ExportButton } from "@/components/admin/export-button";
import { Button } from "@/components/ui/button";
import { TopToolbar } from "../layout/TopToolbar";
import type { Task } from "../types";
import { TaskCreateSheet } from "./TaskCreateSheet";
import { TaskListFilter } from "./TasksListFilter";
import { isDueThisWeek, isOverdue } from "./tasksPredicate";

type Scope = "mine" | "all";
type View = "pending" | "overdue" | "week" | "completed";

export const TaskList = () => {
  const { identity } = useGetIdentity();
  const [scope, setScope] = useState<Scope>("mine");

  return (
    <ListBase
      resource="tasks"
      perPage={1000}
      sort={{ field: "due_date", order: "ASC" }}
      filter={scope === "mine" ? { sales_id: identity?.id } : {}}
    >
      <TaskListContent scope={scope} setScope={setScope} />
    </ListBase>
  );
};

const TaskListContent = ({
  scope,
  setScope,
}: {
  scope: Scope;
  setScope: (scope: Scope) => void;
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState<View>("pending");
  const { data = [], isPending } = useListContext<Task>();

  const tasks = useMemo(
    () =>
      data.filter((task) => {
        const complete = task.status === "completed" || !!task.done_date;
        if (view === "completed") return complete;
        if (complete || task.status === "cancelled") return false;
        if (view === "overdue") return isOverdue(task.due_date);
        if (view === "week") return isDueThisWeek(task.due_date);
        return true;
      }),
    [data, view],
  );

  return (
    <div className="flex flex-col gap-4">
      <TaskCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
      <TopToolbar>
        <ExportButton />
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Nueva tarea
        </Button>
      </TopToolbar>

      <div className="flex flex-wrap gap-2 border-b pb-3">
        <Button
          variant={scope === "mine" ? "default" : "outline"}
          size="sm"
          onClick={() => setScope("mine")}
        >
          Mis tareas
        </Button>
        <Button
          variant={scope === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setScope("all")}
        >
          Todas
        </Button>
        <span className="mx-1 border-l" />
        <Button
          variant={view === "pending" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setView("pending")}
        >
          Pendientes
        </Button>
        <Button
          variant={view === "overdue" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setView("overdue")}
        >
          Vencidas
        </Button>
        <Button
          variant={view === "week" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setView("week")}
        >
          Esta semana
        </Button>
        <Button
          variant={view === "completed" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setView("completed")}
        >
          Completadas
        </Button>
      </div>

      <div className="mx-auto w-full max-w-4xl py-2">
        {isPending ? (
          <p className="text-sm text-muted-foreground">Cargando tareas...</p>
        ) : tasks.length ? (
          <TaskListFilter
            tasks={tasks}
            title={`${tasks.length} tareas`}
            showContact
            isMobile={false}
          />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No hay tareas en esta vista.
          </p>
        )}
      </div>
    </div>
  );
};
